import { StatCard } from "@/components/ui/StatCard";
import { Card } from "@/components/ui/Card";
import { TimeSeriesChart } from "@/components/ui/TimeSeriesChart";
import { LoadingState, ErrorState, DemoDataNotice } from "@/components/ui/AsyncState";
import { useApi } from "@/lib/use-api";
import { formatCurrencyFromCents } from "@/lib/format";
import { Badge } from "@/components/ui/Badge";
import { formatDateTime } from "@/lib/format";
import type {
  PlatformFinanceSummary,
  PlatformFinancePoint,
  PlatformBillingSummary,
  ExpenseCategory,
  SubscriptionStatus,
} from "@platform/shared";

interface FinanceResponse {
  finance: PlatformFinanceSummary;
  timeseries: PlatformFinancePoint[];
  billing: PlatformBillingSummary;
}

const SUBSCRIPTION_STATUS_LABEL: Record<SubscriptionStatus, string> = {
  active: "Active",
  trialing: "Trialing",
  past_due: "Past due",
  canceled: "Canceled",
};

const CATEGORY_LABEL: Record<ExpenseCategory, string> = {
  ai_api: "AI / API costs",
  cloudflare: "Cloudflare costs",
  telephony: "Telephony / SMS costs",
  other: "Other costs",
};

export function Finance() {
  const { data, isLoading, error, refetch } = useApi<FinanceResponse>("/api/admin/finance");

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl text-ink">Finance</h1>
        <p className="mt-1 text-sm text-ink-soft">Revenue, costs, and margin across the platform.</p>
      </div>

      {isLoading && <LoadingState label="Loading financials…" />}
      {error && <ErrorState message={error} onRetry={refetch} />}

      {data && (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <StatCard
              label={`Revenue (${data.finance.rangeDays}d)`}
              value={formatCurrencyFromCents(data.finance.revenueCents)}
            />
            <StatCard
              label={`Expenses (${data.finance.rangeDays}d)`}
              value={formatCurrencyFromCents(data.finance.expensesTotalCents)}
            />
            <StatCard
              label={`Profit (${data.finance.rangeDays}d)`}
              value={formatCurrencyFromCents(data.finance.profitCents)}
            />
            <StatCard label="AI / API costs" value={formatCurrencyFromCents(data.finance.expensesByCategory.ai_api)} />
            <StatCard
              label="Cloudflare costs"
              value={formatCurrencyFromCents(data.finance.expensesByCategory.cloudflare)}
            />
            <StatCard
              label="Telephony / SMS costs"
              value={formatCurrencyFromCents(data.finance.expensesByCategory.telephony)}
            />
            <StatCard label="Other costs" value={formatCurrencyFromCents(data.finance.expensesByCategory.other)} />
            <StatCard
              label="MRR"
              value={formatCurrencyFromCents(data.finance.mrrCents)}
              hint="Estimated from active plan list prices"
            />
          </div>

          <Card className="p-5">
            <p className="mb-4 font-medium text-ink">Revenue vs. expenses ({data.finance.rangeDays}d)</p>
            {data.timeseries.length === 0 ? (
              <p className="py-8 text-center text-sm text-ink-soft">No revenue or expense records in this range.</p>
            ) : (
              <div className="grid gap-6 sm:grid-cols-2">
                <TimeSeriesChart
                  points={data.timeseries.map((p) => ({ label: p.date.slice(5), value: p.revenueCents }))}
                  formatValue={formatCurrencyFromCents}
                  color="#2D4A6D"
                />
                <TimeSeriesChart
                  points={data.timeseries.map((p) => ({ label: p.date.slice(5), value: p.expensesCents }))}
                  formatValue={formatCurrencyFromCents}
                  color="#B45309"
                />
              </div>
            )}
          </Card>

          <Card className="overflow-hidden">
            <div className="border-b border-line px-5 py-3">
              <p className="font-medium text-ink">Expense breakdown ({data.finance.rangeDays}d)</p>
            </div>
            <table className="w-full text-left text-sm">
              <tbody>
                {(Object.keys(CATEGORY_LABEL) as ExpenseCategory[]).map((category) => (
                  <tr key={category} className="border-b border-line last:border-0">
                    <td className="px-5 py-3 text-ink-soft">{CATEGORY_LABEL[category]}</td>
                    <td className="px-5 py-3 text-right font-medium text-ink">
                      {formatCurrencyFromCents(data.finance.expensesByCategory[category])}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          <div className="grid gap-4 sm:grid-cols-4">
            {(Object.keys(SUBSCRIPTION_STATUS_LABEL) as SubscriptionStatus[]).map((status) => (
              <StatCard
                key={status}
                label={`${SUBSCRIPTION_STATUS_LABEL[status]} subscriptions`}
                value={String(data.billing.subscriptionsByStatus[status])}
                hint={status === "active" ? `${data.billing.stripeSubscriptions} billed via Stripe` : undefined}
              />
            ))}
          </div>

          <Card className="overflow-hidden">
            <div className="border-b border-line px-5 py-3">
              <p className="font-medium text-ink">Recent Stripe webhook events</p>
            </div>
            {data.billing.recentWebhookEvents.length === 0 ? (
              <p className="px-5 py-4 text-sm text-ink-soft">No Stripe events received yet.</p>
            ) : (
              <ul className="divide-y divide-line">
                {data.billing.recentWebhookEvents.map((event) => (
                  <li key={event.id} className="flex items-center justify-between px-5 py-3 text-sm">
                    <div>
                      <p className="font-medium text-ink">{event.eventType}</p>
                      <p className="text-xs text-ink-soft">{formatDateTime(event.createdAt)}</p>
                    </div>
                    <Badge tone={event.status === "error" ? "signal" : "neutral"}>{event.status}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <DemoDataNotice>
            Revenue and expenses are real rows from <code>platform_revenue</code> and <code>expenses</code>, kept
            entirely separate from customer organization data. Subscription payments are recorded from Stripe{" "}
            <strong>test-mode</strong> <code>invoice.paid</code> webhooks; other rows are seeded or entered manually.
            MRR is still an estimate from active subscriptions' plan list prices, not a billed figure.
          </DemoDataNotice>
        </>
      )}
    </div>
  );
}
